import React, { useMemo, useCallback } from 'react';

import { AboutThisEventModal } from './AboutThisEventModal';
import type { StructuredContent } from '@/types';
import { AboutThisEventEmbedded } from './AboutThisEventEmbedded';
import { StructuredModuleRenderer } from './StructuredModuleRenderer';
import { MEDIA_QUERIES } from '@/components/_shared/constants';
import { useMediaQuery } from '@/hooks';

export type AboutThisEventProps = {
  showFullEventOverview: boolean;
  structuredContent?: StructuredContent;
  handleCloseModal: () => void;
};

export const AboutThisEvent: React.FC<AboutThisEventProps> = React.memo(
  ({ showFullEventOverview, structuredContent, handleCloseModal }) => {
    const isNarrowScreen = useMediaQuery(`(max-width: ${MEDIA_QUERIES.MEDIUM_BREAKPOINT}px)`);

    const memoizedStructuredContent = useMemo(() => structuredContent, [structuredContent]);

    const renderModules = useCallback(
      (isModal: boolean, keyPrefix: string) =>
        memoizedStructuredContent?.modules?.map((module, index) => (
          <StructuredModuleRenderer key={`${keyPrefix}-${index}`} module={module} isModal={isModal} />
        )) ?? null,
      [memoizedStructuredContent?.modules],
    );

    const modalChildren = useMemo(() => renderModules(true, 'modal'), [renderModules]);

    const embeddedChildren = useMemo(() => renderModules(false, 'embedded'), [renderModules]);

    const aboutThisEventModal =
      isNarrowScreen && showFullEventOverview ? (
        <AboutThisEventModal visible={showFullEventOverview} onClose={handleCloseModal}>
          {modalChildren}
        </AboutThisEventModal>
      ) : null;

    return (
      <>
        {aboutThisEventModal}
        <AboutThisEventEmbedded visible={showFullEventOverview}>{embeddedChildren}</AboutThisEventEmbedded>
      </>
    );
  },
);

AboutThisEvent.displayName = 'AboutThisEvent';
